import {
  HttpErrorResponse,
  HttpEvent,
  HttpInterceptorFn,
  HttpResponse,
  HttpStatusCode,
} from '@angular/common/http';
import { catchError, Observable, of, throwError } from 'rxjs';

/**
 * Interceptor that normalizes backend error responses to provide better user experience
 * and security by transforming specific error messages into more user-friendly formats.
 *
 * Transformations include:
 * 1. Password Reset: Transforms "user not found" errors into success responses to prevent email enumeration
 * 2. Login: Transforms generic "Invalid credentials" errors into descriptive messages for users
 *
 * According to security best practices, we should not reveal whether an email
 * exists in the database, and we should provide clear feedback for authentication failures.
 */
export const errorNormalizationInterceptor: HttpInterceptorFn = (req, next) => {
  return next(req).pipe(
    catchError((error: HttpErrorResponse): Observable<HttpEvent<unknown>> => {
      if (req.url.includes('/api/auth/password/forgot')) {
        if (
          error.status === HttpStatusCode.UnprocessableEntity &&
          error.error?.message ===
            "We can't find a user with that email address."
        ) {
          const successResponse = new HttpResponse({
            status: 200,
            body: {
              success: true,
              message: 'Password reset email sent successfully',
              data: {
                message: 'Password reset email sent successfully',
              },
            },
            headers: error.headers,
            url: req.url,
          });

          return of(successResponse);
        }
      }

      if (req.url.includes('/api/auth/login')) {
        if (
          error.status === HttpStatusCode.Unauthorized &&
          error.error?.error?.includes('Invalid credentials')
        ) {
          const enhancedError: typeof error & {
            userMessage: string;
          } = {
            ...error.error,
            userMessage:
              'El correo electrónico o la contraseña son incorrectos.',
          };

          return throwError(() => enhancedError);
        }
      }

      return throwError(() => error);
    }),
  );
};
